/**
 * Cadastro direto de usuário (sem convite e sem e-mail obrigatório): nome, usuário de acesso,
 * senha inicial, papel, situação e e-mail opcional. A pessoa entra com usuário + código da
 * empresa + senha inicial e troca a senha no primeiro acesso.
 *
 * Também define o código de acesso da empresa quando ele ainda não existe (uma vez: depois que
 * há usuários, o código não muda). A senha digitada aqui só vai para a API: não fica na tela
 * depois de salvar.
 */
import {
  PASSWORD_MIN_LENGTH,
  createLoginUserSchema,
  loginCodeSchema,
  type CreatedLoginUser,
} from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useCan } from "@/app/me";
import { Alert, Button, Card, Field, Input, Select, Spinner } from "@/components/ui";
import { apiFetch, errorMessage } from "@/lib/api";
import { empresaDaInstalacao } from "@/lib/empresa-login";
import { generateTemporaryPassword } from "./temporary-password";

type Role = { id: string; key: string; name: string };
type DirectConfig = { available: boolean; loginCode: string | null };

const EMPTY = {
  fullName: "",
  username: "",
  password: "",
  roleId: "",
  active: "true",
  contactEmail: "",
};

export function NewUserPanel({ roles, onClose }: { roles: Role[]; onClose: () => void }) {
  const can = useCan();
  const queryClient = useQueryClient();
  const config = useQuery({
    queryKey: ["usuarios", "cadastro-direto"],
    queryFn: () => apiFetch<DirectConfig>("/usuarios/cadastro-direto"),
  });
  const [form, setForm] = useState(EMPTY);
  const [code, setCode] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedLoginUser | null>(null);

  const saveCode = useMutation({
    mutationFn: (loginCode: string) =>
      apiFetch<{ loginCode: string }>("/empresa/codigo-acesso", {
        method: "PUT",
        body: { loginCode },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["usuarios", "cadastro-direto"] }),
  });

  const create = useMutation({
    mutationFn: (body: unknown) =>
      apiFetch<CreatedLoginUser>("/usuarios", { method: "POST", body }),
    onSuccess: async (user) => {
      setCreated(user);
      setForm(EMPTY);
      await queryClient.invalidateQueries({ queryKey: ["usuarios"] });
    },
  });

  const set = (k: keyof typeof EMPTY) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });

  function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setCreated(null);
    const parsed = createLoginUserSchema.safeParse({
      ...form,
      active: form.active === "true",
    });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? "Confira os dados.");
      return;
    }
    create.mutate(parsed.data);
  }

  function submitCode(e: FormEvent) {
    e.preventDefault();
    const parsed = loginCodeSchema.safeParse(code);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? "Código inválido.");
      return;
    }
    setFormError(null);
    saveCode.mutate(parsed.data);
  }

  const close = (
    <Button size="sm" variant="ghost" onClick={onClose}>
      Fechar
    </Button>
  );

  if (config.isPending)
    return (
      <Card title="Novo usuário" actions={close}>
        <Spinner />
      </Card>
    );
  if (config.isError)
    return (
      <Card title="Novo usuário" actions={close}>
        <Alert tone="error">{errorMessage(config.error)}</Alert>
      </Card>
    );
  if (!config.data.available)
    return (
      <Card title="Novo usuário" actions={close}>
        <Alert tone="warn">
          O cadastro direto de usuários ainda não está ativo neste servidor. Enquanto isso, use
          Administração → Convites.
        </Alert>
      </Card>
    );

  const loginCode = config.data.loginCode;
  if (!loginCode)
    return (
      <Card title="Novo usuário" actions={close}>
        <p className="mb-4 text-sm text-muted-foreground">
          Antes do primeiro usuário, defina o <strong>código de acesso da empresa</strong>. Os
          funcionários entram com usuário + este código + senha. Use algo curto e fácil de digitar
          (ex.: grao21). Depois que houver usuários, o código não pode mais ser trocado.
        </p>
        {(formError || saveCode.isError) && (
          <Alert tone="error">{formError ?? errorMessage(saveCode.error)}</Alert>
        )}
        {can("admin.empresa.editar") ? (
          <form onSubmit={submitCode} className="mt-3 flex items-end gap-2">
            <div className="flex-1">
              <Field label="Código de acesso da empresa">
                <Input
                  autoCapitalize="none"
                  spellCheck={false}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </Field>
            </div>
            <Button type="submit" disabled={saveCode.isPending}>
              Definir código
            </Button>
          </form>
        ) : (
          <Alert tone="warn">Peça a quem edita os dados da empresa para definir o código.</Alert>
        )}
      </Card>
    );

  // nesta instalação (ERP com empresa fixa) o login da empresa é só usuário + senha
  const semCodigo = empresaDaInstalacao() === loginCode;

  return (
    <Card title="Novo usuário" actions={close}>
      <p className="mb-4 text-sm text-muted-foreground">
        {semCodigo ? (
          <>
            A pessoa entra com o <strong>usuário</strong> e a senha inicial, e cria a própria senha
            no primeiro acesso.
          </>
        ) : (
          <>
            A pessoa entra com o <strong>usuário</strong>, o código da empresa{" "}
            <strong>{loginCode}</strong> e a senha inicial, e cria a própria senha no primeiro
            acesso.
          </>
        )}{" "}
        Não precisa de e-mail nem de convite.
      </p>
      {created && (
        <Alert tone="ok">
          Usuário <strong>{created.username}</strong> criado
          {created.status === "active" ? "" : " (inativo)"}. Para entrar: usuário{" "}
          <strong>{created.username}</strong>
          {semCodigo ? (
            " e a senha inicial informada"
          ) : (
            <>
              , código <strong>{created.loginCode}</strong> e a senha inicial informada
            </>
          )}
          . A troca de senha será pedida no primeiro acesso.
        </Alert>
      )}
      {(formError || create.isError) && (
        <div className="mt-3">
          <Alert tone="error">{formError ?? errorMessage(create.error)}</Alert>
        </div>
      )}
      <form onSubmit={submit} className="mt-3 grid gap-4 sm:grid-cols-2">
        <Field label="Nome completo" required>
          <Input value={form.fullName} onChange={set("fullName")} autoComplete="off" />
        </Field>
        <Field
          label="Usuário de acesso"
          required
          hint="Ex.: vendedor01 — letras minúsculas e números."
        >
          <Input
            value={form.username}
            onChange={set("username")}
            autoCapitalize="none"
            spellCheck={false}
            autoComplete="off"
          />
        </Field>
        <Field
          label="Senha inicial"
          required
          hint={`Mínimo de ${PASSWORD_MIN_LENGTH} caracteres. Entregue à pessoa com segurança.`}
        >
          <div className="flex gap-2">
            <Input
              value={form.password}
              onChange={set("password")}
              autoComplete="new-password"
              spellCheck={false}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => setForm({ ...form, password: generateTemporaryPassword() })}
            >
              Gerar
            </Button>
          </div>
        </Field>
        <Field label="Papel" required>
          <Select value={form.roleId} onChange={set("roleId")}>
            <option value="">Selecione…</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Situação" required>
          <Select value={form.active} onChange={set("active")}>
            <option value="true">Ativo</option>
            <option value="false">Inativo</option>
          </Select>
        </Field>
        <Field label="E-mail (opcional)" hint="Só para contato; o login é pelo usuário.">
          <Input
            type="email"
            value={form.contactEmail}
            onChange={set("contactEmail")}
            autoComplete="off"
          />
        </Field>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Salvando…" : "Cadastrar usuário"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
